import { useEffect, useState } from 'react';
import { decideAccess, GeoDecision } from '../utils/geoAccess';

export type GeoStatus = 'checking' | 'allowed' | 'blocked';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Only enforce on the live domain, so previews and local work are never blocked. */
const ENFORCED_HOST = 'fulldetailpro.com';
/** Free, keyless, CORS-enabled IP lookup. Returns approximate country/region/coordinates only. */
const GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/geo.json';
/** Versioned so sessions cached under the old California-wide rule are re-checked. */
const CACHE_KEY = 'mpd_geo_status_v2';
const TIMEOUT_MS = 3000;
/**
 * Review pass: opening any page with ?site_review=mpd-scan lets site-review
 * tools (e.g. Magic Patterns' website scanner) and the site manager through,
 * wherever they connect from. The pass is remembered on that browser.
 */
const REVIEW_PARAM = 'site_review';
const REVIEW_TOKEN = 'mpd-scan';
const REVIEW_STORAGE_KEY = 'mpd_review_pass';

function hasReviewPass(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get(REVIEW_PARAM) === REVIEW_TOKEN) {
      localStorage.setItem(REVIEW_STORAGE_KEY, '1');
      return true;
    }
    return localStorage.getItem(REVIEW_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
/** Search engines, social previews, monitoring and audit tools always get the full site. */
const CRAWLER_PATTERN = /bot|crawl|spider|slurp|mediapartners|adsbot|google-inspectiontool|bingpreview|duckduck|baidu|yandex|facebookexternalhit|twitterbot|linkedinbot|embedly|lighthouse|pagespeed|gtmetrix|headless|uptime|pingdom|monitor|statuscake/i;

interface GeoResponse {
  country_code?: string;
  region?: string;
  latitude?: string;
  longitude?: string;
}

/**
 * Ventura County + Los Angeles County + all of Oregon are allowed; everything
 * else is blocked. Fails open: if the lookup errors, times out, or can't
 * determine the location, the visitor is allowed. The IP is never stored or
 * displayed.
 */
export function useGeoAccess(): GeoStatus {
  const [status, setStatus] = useState<GeoStatus>(() => {
    if (typeof window === 'undefined') return 'allowed';
    if (!window.location.hostname.endsWith(ENFORCED_HOST)) return 'allowed';
    if (CRAWLER_PATTERN.test(navigator.userAgent)) return 'allowed';
    if (hasReviewPass()) return 'allowed';
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached === 'allowed' || cached === 'blocked') return cached;
    return 'checking';
  });

  useEffect(() => {
    if (status !== 'checking') return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

    fetch(GEO_ENDPOINT, {
      signal: controller.signal
    }).then(res => res.ok ? res.json() as Promise<GeoResponse> : Promise.reject()).then(geo => {
      const country = geo.country_code?.toUpperCase();
      const region = geo.region?.trim();
      const decision = decideAccess({
        country,
        region,
        lat: geo.latitude ? parseFloat(geo.latitude) : undefined,
        lon: geo.longitude ? parseFloat(geo.longitude) : undefined
      });
      finish(decision, country === 'US' ? region : country);
    }).catch(() => finish('unknown', undefined)).finally(() => window.clearTimeout(timer));

    function finish(decision: GeoDecision, geoArea: string | undefined) {
      const next = decision === 'blocked' ? 'blocked' : 'allowed';
      sessionStorage.setItem(CACHE_KEY, next);
      window.gtag?.('event', 'geo_check', {
        geo_status: decision,
        geo_area: geoArea ?? 'unknown'
      });
      setStatus(next);
    }

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [status]);

  return status;
}
